<template>
  <button
    v-if="alert"
    ref="alert"
    @click="takeToAlerts"
    @touchstart="startMove"
    @mousedown.stop="startMouseDown"
    @touchmove="moveAlert"
    @mousemove.stop="dragAlert"
    @touchend="stopMove"
    class="alert pointer-events-auto cursor-pointer flex rounded-lg bg-white hover:bg-white-off transition-color ring-1 ring-black ring-opacity-10 shadow-xl mb-2"
    :class="timeSinceMouseDown < 2 ? 'active:bg-gray-100' : 'cursor-grabbing'"
    :style="alertTransform"
  >
    <div v-if="!isAggregate" class="w-full flex items-start p-4">
      <div class="flex flex-col w-full sm:items-start text-left">
        <div class="flex w-full items-center font-medium text-gray-800 dark:text-white/80 sm:ml-3">
          <div v-if="priority" class="text-xs font-semibold leading-5 mr-1.5">
            <p :class="priorityColor" class="inline-flex rounded-full items-center px-2">
              <span>
                <font-awesome-icon v-if="alert.event_level === 'emerg'" icon="fa-solid fa-fire" class="mr-1 w-3 h-3 text-xs" />
                <font-awesome-icon v-if="alert.event_level === 'alert'" icon="fa-solid fa-light-emergency-on" class="mr-1 w-3 h-3 text-xs" />
                <font-awesome-icon v-if="alert.event_level === 'crit'" icon="fa-solid fa-chevrons-up" class="mr-1 w-3 h-3 text-xs" />
                <font-awesome-icon v-if="alert.event_level === 'err'" icon="fa-solid fa-triangle-exclamation" class="mr-1 w-3 h-3 text-xs" />
                <font-awesome-icon v-if="alert.event_level === 'warning'" icon="fa-solid fa-circle-exclamation" class="mr-1 w-3 h-3 text-xs" />
                <font-awesome-icon v-if="alert.event_level === 'notice'" icon="fa-solid fa-flag-swallowtail" class="mr-1 w-3 h-3 text-xs" />
                <font-awesome-icon v-if="alert.event_level === 'info'" icon="fa-solid fa-circle-info" class="mr-1 w-3 h-3 text-xs" />
                <font-awesome-icon v-if="alert.event_level === 'debug'" icon="fa-bug fa-solid" class="mr-1 w-3 h-3 text-xs" />
              </span>
              {{ priority }}
            </p>
          </div>
          <p class="truncate">
            {{ alert.subject }}
          </p>
          <p class="ml-1 sm:ml-2 mt-0.5 sm:mt-0 text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400">
            {{ alert.type }}
          </p>
        </div>
        <section class="flex flex-wrap items-end ml-3 sm:ml-6">
          <p v-if="alert.message" class="w-full mt-3 text-sm text-gray-800 dark:text-white/80 text-left whitespace-pre-line break-words">
            {{ alert.message }}
          </p>
          <!-- <div class="mt-3 flex space-x-7">
            <button type="button" class="rounded-md text-sm font-medium text-primary hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">Dismiss</button>
            <button type="button" class="rounded-md bg-white text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">Dismiss</button>
          </div> -->
        </section>
      </div>
      <div class="hidden ml-4 sm:flex flex-shrink-0">
        <button
          type="button"
          @click.stop="closeAlert"
          ref="closeBtn"
          class="inline-flex rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <span class="sr-only">Close</span>
          <font-awesome-icon icon="fa-solid fa-xmark" class="h-4.5 w-4.5" aria-hidden="true" />
        </button>
      </div>
    </div>
    <div v-else class="w-full flex justify-center items-center font-medium py-4">
      <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="w-6 h-6 mr-2 text-primary-500" />
      <p>
        You have <span class="font-semibold text-primary-700">{{ alert.count }}</span> new alerts
      </p>
    </div>
  </button>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'

export default {
  name: "CornerAlert",
  props: {
    alert: {
      type: Object,
      required: true
    },
    idx: {
      type: Number,
      required: true
    },
  },
  data() {
    return {
      isMoving: false,
      touchStart: { x: 0, y: 0 },
      moveY: 0,
      moveX: 0,
      alertTransform: "",
      timeSinceMouseDown: 0,
      timerInterval: null
    }
  },
  computed: {
    ...mapState(["screen"]),
    ...mapGetters(["modalOpen"]),
    isAggregate() {
      return this.alert.isAggregate
    },
    priority() {
      let keywords = {
        "emerg": "EMERGENCY",
        "alert": "Alert",
        "crit": "Critical",
        "err" : "Error",
        "warning": "Warning",
        "notice": "Notice",
        "info": "Info",
        "debug": "Debug"
      }
      return keywords[this.alert.event_level]
    },
    priorityColor() {
      let colors = {
        "emerg": "bg-red-600 text-white mt-0.5",
        "alert": "bg-red-500 text-white",
        "crit": "bg-red-300 text-red-900 mt-0.5",
        "err" : "bg-yellow-500 text-white mt-0.5",
        "warning": "bg-yellow-200 text-yellow-700 mt-0.5",
        "notice": "bg-yellow-100 text-yellow-800 border border-yellow-800 mt-0.5",
        "info": "bg-blue-100 text-blue-800 border border-blue-800 mt-0.5",
        "debug": "bg-indigo-100 text-indigo-800 border border-indigo-800"
      }
      return colors[this.alert.event_level]
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
  },
  mounted() {
    if (!this.screen.sm) setTimeout(() => {
      if (!this.isMoving) this.closeAlert()
    }, 3000)
  },
  methods: {
    ...mapMutations([
      "moveAlertToInitialBatch",
      "closePrompt",
      "closeModal"
    ]),
    async takeToAlerts() {
      // on direct click (not moving the alert around), take to Alerts page
      if (this.timeSinceMouseDown < 3 && (this.screen.sm ? Math.abs(this.moveX) < 15 : Math.abs(this.moveY) < 15)) {
        const needTimeout = this.modalOpen
        this.closePrompt()
        this.closeModal()
        if (needTimeout) await this.$nextTick()
        if (this.isAggregate) this.$router.push("/Alerts")
        else this.$router.push(`/Alerts?id=${this.alert.id}`)
      }
    },
    closeAlert() {
      this.moveAlertToInitialBatch(this.alert)
    },
    startMove(e) {
      e.preventDefault()
      this.isMoving = true
      this.timeSinceMouseDown = 0
      this.timerInterval = setInterval(() => {
        this.timeSinceMouseDown++
      }, 100)
      this.touchStart.x = e.touches[0].clientX
      this.touchStart.y = e.touches[0].clientY
    },
    startMouseDown(e) {
      e.preventDefault()
      this.isMoving = true
      this.timeSinceMouseDown = 0
      this.timerInterval = setInterval(() => {
        this.timeSinceMouseDown++
      }, 100)
      // watch out for mouse release of alert, even when "losing" pointer contact
      window.addEventListener('mouseup', this.stopMove)
      this.touchStart.x = e.clientX
      this.touchStart.y = e.clientY
    },
    moveAlert(e) {
      if (this.isMoving) {
        // get the current position of the user's finger
        const x = e.touches[0].clientX
        const y = e.touches[0].clientY

        // calculate the distance that the user has moved their finger
        const dx = x - this.touchStart.x
        const dy = y - this.touchStart.y

        // allow a tiny bit of "bounce", but otherwise alert cannot be brought down
        this.moveX = Math.max(dx, -20)
        this.moveY = Math.min(dy, 20)

        // move alert based on user finger direction
        if (!this.screen.sm) this.alertTransform = `transform: translateY(${this.moveY}px);`
        else this.alertTransform = `transform: translateX(${this.moveX}px);`
      }
    },
    dragAlert(e) {
      e.preventDefault()
      if (this.isMoving) {
        // get the current position of the user's finger
        const x = e.clientX
        const y = e.clientY

        // calculate the distance that the user has moved their finger
        const dx = x - this.touchStart.x
        const dy = y - this.touchStart.y

        // allow a tiny bit of "bounce", but otherwise alert cannot be brought down
        this.moveX = Math.max(dx, -20)
        this.moveY = Math.min(dy, 20)

        // move alert based on user finger direction
        if (!this.screen.sm) this.alertTransform = `transform: translateY(${this.moveY}px);`
        else this.alertTransform = `transform: translateX(${this.moveX}px);`
        
      }
    },
    async stopMove(e) {
      e.preventDefault()
      e.stopPropagation()

      // needed to close alert on mobile (touchstart overtakes click command)
      if (this.$refs.closeBtn && this.$refs.closeBtn.contains(e.target)) {
        this.closeAlert()
        return
      }
      // needed to go to alert on mobile (touchstart overtakes click command)
      if (this.isMobile && this.timeSinceMouseDown < 3) {
        this.takeToAlerts()
        return
      }

      setTimeout(() => this.isMoving = false, 100)
      if (!this.screen.sm) setTimeout(() =>  { if (!this.isMoving) this.closeAlert() }, 3000)

      this.timeSinceMouseDown = 0
      clearInterval(this.timerInterval)
      window.removeEventListener('mouseup', this.stopMove)

      await this.$nextTick()

      setTimeout(() => {

        if (!this.screen.sm) {
          // if user has flicked the alert up away, move it up and close
          if (this.moveY < -40) {
            this.alertTransform = `transform: translateY(-150px);
            transition-property: transform;
            transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
            transition-duration: 300ms;`
            setTimeout(() => this.closeAlert(), 305)
          }
          else {
            // if user has moved the alert but hasn't fully flicked it away, return to original position
            this.moveY = 0
            this.alertTransform = `transform: translateY(0px);
              transition-property: transform;
              transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
              transition-duration: 200ms;
            `
            // reset transition property to only affect colors
            setTimeout(() => this.alertTransform = `transform: translateY(0px);
              transition-property: background-color, border-color, color, fill, stroke;
              transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
              transition-duration: 150ms;
            `, 201)
          }
        }
  
        else {
          
          // if user has dragged the alert up all the way to the right, move it up and close
          if (this.moveX > 150) {
            this.alertTransform = `transform: translateX(1200px);
            transition-property: transform;
            transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
            transition-duration: 300ms;`
            setTimeout(() => this.closeAlert(), 305)
          }
          else {
            // if user has moved the alert but not fully to the right, return to original position
            this.moveX = 0
            this.alertTransform = `transform: translateX(0px);
              transition-property: transform;
              transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
              transition-duration: 200ms;
            `
            // reset transition property to only affect colors
            setTimeout(() => this.alertTransform = `transform: translateX(0px);
              transition-property: background-color, border-color, color, fill, stroke;
              transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
              transition-duration: 150ms;
            `, 201)
          }
        }

      }, 10)

    }
  },
}
</script>

<style scoped>
.alert {
  width: 100%;
}
@media screen and (min-width: 640px) {
  .alert {
    width: 32rem;
  }
}
</style>